<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from '../../../services/i18n';
	import {
		ActionsContainer,
		IconButton,
		ListMenuCard,
		ListMenuCardV2,
	} from '@Shelly/shelly-design-system/index';
	import RoomLabelMain from './RoomLabelMain.svelte';
	import RoomLabelList from './RoomLabelList.svelte';
	import { edit } from 'src/stores/ui/edit_lists';
	import { longPressEditAvailable } from 'src/stores/features/dev/long-press-edit';
	import { buildListPressHoldOptions } from 'src/helpers/long_press_edit_helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { mapLegacyColorToToken } from 'src/helpers/new-layout-helpers';
	import { updateItemStoreAfterPulse } from 'src/helpers/add-entity-helpers';

	/** When true, render ListMenuCardV2 hollow bones (no `item` required). */
	export let skeleton = false;
	export let item;
	export let onCardClick;
	export let load_image_from;
	export let onCardDelete;
	export let onCardEdit;

	const dispatch = createEventDispatcher();
	let selected = false;

	$: pressHoldOptions = buildListPressHoldOptions($edit, $longPressEditAvailable, {
		onEntered: () => dispatch('handle-drag-start'),
	});

	$: if (!skeleton && item?.new && $edit) {
		// Remove pulse when added new item and we are in edit mode
		updateItemStoreAfterPulse(item, 'rooms');
	}
</script>

{#if skeleton}
	<ListMenuCardV2 skeleton type="extended" editMode={false} selected={false} />
{:else if $showNewLayout}
	<ListMenuCardV2
		title={item.name}
		image={item.image && load_image_from(item.image)}
		backgroundColor={mapLegacyColorToToken(item.backgroundColor)}
		editMode={$edit}
		{pressHoldOptions}
		on:click={() => {
			selected = !selected;
			onCardClick(item);
		}}
		hideSlotRight={!$edit}
		isFocusable
		{selected}
		on:handle-drag-start
		on:handle-drag-end
		pulseAnimation={item?.new}
		on:pulse-animation-end={async () => {
			updateItemStoreAfterPulse(item, 'rooms');
		}}
	>
		<RoomLabelMain {item} slot="titleRight" on:label-click={() => onCardClick(item)} />

		<RoomLabelList {item} size="m" slot="main" on:label-click={() => onCardClick(item)} />
	</ListMenuCardV2>
{:else}
	<ListMenuCard
		title={item.name}
		image={item.image && load_image_from(item.image)}
		backgroundColor={item.backgroundColor}
		editMode={$edit}
		on:click={() => onCardClick(item)}
		hideSlotRight={!$edit}
		isFocusable={false}
		pulseAnimation={item?.new}
		on:pulse-animation-end={async () => {
			updateItemStoreAfterPulse(item, 'rooms');
		}}
	>
		<RoomLabelMain {item} slot="titleRight" on:label-click={() => onCardClick(item)} />

		<RoomLabelList {item} size="m" slot="main" on:label-click={() => onCardClick(item)} />

		<svelte:fragment slot="right">
			{#if $edit}
				<ActionsContainer orientation="vertical">
					<IconButton
						on:mousedown
						on:touchstart
						on:mouseup
						on:touchend
						icon="fas fa-up-down-left-right"
						slot="first"
						dndHandle
						aria-label={$format('aria_drag_room', {
							default: 'Drag room',
						})}
					/>
					<IconButton
						icon="fas fa-pen"
						slot="second"
						on:click={(event) => onCardEdit(event, item)}
						aria-label={$format('aria_edit_room', {
							default: 'Edit room',
						})}
					/>
					<IconButton
						icon="fas fa-trash"
						variant="danger"
						slot="third"
						on:click={(event) => onCardDelete(event, item)}
						aria-label={$format('aria_delete_room', {
							default: 'Delete room',
						})}
					/>
				</ActionsContainer>
			{/if}
		</svelte:fragment>
	</ListMenuCard>
{/if}
